/* global chrome */

import { EventType } from '../client/types';
import { SocketError } from '../exceptions';

import { close, connect, create, messageForResultCode, write } from './chrome-tcp-utils';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const noop = (..._: any[]): void => void 0;

export default class Socket {
  public socketId?: number;

  private closed: boolean = true;

  private closeListeners: Array<() => void> = [];

  private dataListeners: Array<(data: Buffer) => void> = [];

  private errorListeners: Array<(err: Error) => void> = [];

  constructor(readonly autoResume = false) {}

  public async create() {
    const socketId = await create();
    this.socketId = socketId;
    this.closed = false;
  }

  public async destroy() {
    if (this.closed || !this.socketId) {
      return;
    }
    await close(this.socketId);
    this.invokeListeners(this.closeListeners, undefined);
  }

  public async connect(port: number, host: string, onConnect?: () => void) {
    if (!this.socketId) {
      throw new SocketError('Invalid socket instance');
    }

    chrome.sockets.tcp.onReceive.addListener(eventInfo => {
      if (eventInfo.socketId === this.socketId) {
        const data = Buffer.from(eventInfo.data);
        this.onData(data);
      }
    });

    chrome.sockets.tcp.onReceiveError.addListener(eventInfo => {
      if (eventInfo.socketId === this.socketId) {
        const error = messageForResultCode(eventInfo.resultCode);
        this.onError(new SocketError(error));
        if (this.autoResume) {
          chrome.sockets.tcp.setPaused(this.socketId, false);
        }
      }
    });

    await connect(this.socketId, port, host);
    if (typeof onConnect === 'function') {
      onConnect();
    }
  }

  public off(eventType: EventType, callback: ((data: Buffer) => void) | ((err: Error) => void)) {
    switch (eventType) {
      case EventType.DATA:
        this.dataListeners = this.dataListeners.filter(fn => fn !== callback);
        break;
      case EventType.ERROR:
        this.errorListeners = this.errorListeners.filter(fn => fn !== callback);
        break;
      case EventType.CLOSE:
        this.closeListeners = this.closeListeners.filter(fn => fn !== callback);
        break;
      default:
        return noop(callback);
    }
  }

  public on(eventType: EventType, callback: ((data: Buffer) => void) | ((err: Error) => void)) {
    switch (eventType) {
      case EventType.DATA:
        this.dataListeners = this.dataListeners.concat(callback as (data: Buffer) => void);
        break;
      case EventType.ERROR:
        this.errorListeners = this.errorListeners.concat(callback as (err: Error) => void);
        break;
      case EventType.CLOSE:
        this.closeListeners = this.closeListeners.concat(callback as () => void);
        break;
      default:
        return noop(callback);
    }
  }

  public write(message: Buffer) {
    if (!this.socketId) {
      throw new SocketError('Socket initialization failed.');
    }
    if (this.closed) {
      throw new SocketError('Socket is closed.');
    }
    const arrayBuffer = message.buffer;
    return write(this.socketId, arrayBuffer);
  }

  private onData(data: Buffer) {
    this.invokeListeners(this.dataListeners, data);
  }

  private onError(error: Error) {
    this.invokeListeners(this.errorListeners, error);
  }

  private invokeListeners<D>(listenerAry: Array<(d: D) => void>, data: D) {
    listenerAry.forEach(listener => listener(data));
  }
}
